// src/components/ui/ExpenseSearch.js
// Shared expense search box used by the Weekly, Monthly, Annual and Planner
// pages. Type to filter, then click a match (or press Enter for the first
// one) and the page smooth-scrolls to that expense row and flashes it.
//
// Rows are located by their `data-expense-id` attribute, so each page passes
// items whose `key` matches what its rows render. `onBeforeNavigate(item)`
// lets a page reveal the row first (e.g. switch to All Weeks or un-hide $0
// rows) before the scroll happens.
import React, { useEffect, useRef, useState } from 'react';

const HIT_CLASS = 'expense-search-hit';

const ExpenseSearch = ({ items = [], onBeforeNavigate, placeholder = 'Search expenses…' }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const trimmed = query.trim().toLowerCase();
  const matches = trimmed
    ? items.filter(it => (it.label || '').toLowerCase().includes(trimmed)).slice(0, 8)
    : [];

  // Close the dropdown when clicking anywhere else on the page.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const navigateTo = (item) => {
    setOpen(false);
    setQuery(item.label);
    if (onBeforeNavigate) onBeforeNavigate(item);

    // Give the page a moment to re-render (filters may have just changed)
    // before locating and scrolling to the row.
    setTimeout(() => {
      const key = String(item.key);
      const esc = (window.CSS && window.CSS.escape) ? window.CSS.escape(key) : key.replace(/"/g, '\\"');
      const el = document.querySelector(`[data-expense-id="${esc}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add(HIT_CLASS);
      setTimeout(() => el.classList.remove(HIT_CLASS), 2400);
    }, 250);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (matches.length > 0) navigateTo(matches[0]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="expense-search" ref={wrapRef}>
      <style>{`
        .expense-search {
          position: relative;
          min-width: 220px;
        }

        .expense-search-input {
          width: 100%;
          padding: 8px 12px 8px 32px;
          border: 1px solid var(--input-border);
          border-radius: 6px;
          font-size: 0.9rem;
          background-color: var(--input-bg);
          color: var(--text-primary);
          box-sizing: border-box;
          transition: all 0.2s ease;
        }

        .expense-search-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.15);
        }

        .expense-search-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.85rem;
          pointer-events: none;
          opacity: 0.7;
        }

        .expense-search-list {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          margin: 0;
          padding: 4px;
          list-style: none;
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
          z-index: 500;
          max-height: 320px;
          overflow-y: auto;
        }

        .expense-search-option {
          display: flex;
          flex-direction: column;
          gap: 1px;
          width: 100%;
          padding: 8px 10px;
          border: none;
          border-radius: 6px;
          background: transparent;
          color: var(--text-primary);
          text-align: left;
          cursor: pointer;
          font-size: 0.88rem;
        }

        .expense-search-option:hover,
        .expense-search-option:focus {
          background: var(--hover-bg);
          outline: none;
        }

        .expense-search-option-label {
          font-weight: 600;
        }

        .expense-search-option-sub {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .expense-search-empty {
          padding: 10px 12px;
          font-size: 0.85rem;
          font-style: italic;
          color: var(--text-muted);
        }

        /* Flash applied to the located row (div or tr) after scrolling. */
        .expense-search-hit,
        .expense-search-hit > td {
          background-color: rgba(33, 150, 243, 0.18) !important;
          transition: background-color 0.4s ease;
        }

        .expense-search-hit {
          outline: 3px solid var(--primary);
          outline-offset: -2px;
          animation: expenseSearchPulse 1.1s ease-in-out 2;
        }

        @keyframes expenseSearchPulse {
          50% { outline-color: rgba(33, 150, 243, 0.35); }
        }
      `}</style>

      <span className="expense-search-icon" aria-hidden="true">🔍</span>
      <input
        type="text"
        className="expense-search-input"
        role="searchbox"
        aria-label="Search expenses"
        placeholder={placeholder}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {open && trimmed && (
        matches.length > 0 ? (
          <ul className="expense-search-list">
            {matches.map(item => (
              <li key={item.key}>
                <button
                  type="button"
                  className="expense-search-option"
                  onClick={() => navigateTo(item)}
                >
                  <span className="expense-search-option-label">{item.label}</span>
                  {item.sublabel && (
                    <span className="expense-search-option-sub">{item.sublabel}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="expense-search-list expense-search-empty">
            No expenses match “{query.trim()}”
          </div>
        )
      )}
    </div>
  );
};

export default ExpenseSearch;
